// ---------------------------------------------------------------------------
// HOW A NUMBER BECOMES TEXT (DESIGN.md §3). Universe finance is Italy-first:
// every figure is formatted with Intl.NumberFormat('it-IT') whatever language
// the interface speaks — €12.400,00 · 34,2% · 1.240,5 h · 1.284.
//
// This module is the only place a number becomes text; the `numeral` helpers
// in formatNumber.js are the en-US ones the migration retires list by list.
// ---------------------------------------------------------------------------

const LOCALE = 'it-IT';

// CLDR gives Italian a minimum of two grouping digits, so a plain it-IT
// formatter writes 4200 without its point. The figures here are grouped from
// four digits (€4.200,00), which `useGrouping: 'always'` asks for; an engine
// that does not know that value falls back to the German pattern, which has
// the same point and comma and groups from four digits.
const make = (options) => {
  try {
    const formatter = new Intl.NumberFormat(LOCALE, {
      ...options,
      useGrouping: 'always'
    });
    if (formatter.format(4200).includes('.')) return formatter;
  } catch (error) {
    /* an older engine: fall through */
  }
  return new Intl.NumberFormat('de-DE', options);
};

const money = make({ minimumFractionDigits: 2, maximumFractionDigits: 2 });
const oneDecimal = make({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
const integer = make({ maximumFractionDigits: 0 });
const unitPrice = make({ minimumFractionDigits: 2, maximumFractionDigits: 3 });
// a file size: one decimal where it carries information, none where it does
// not — "7,3 MB" and "500 MB", never "500,0 MB"
const upToOne = make({ minimumFractionDigits: 0, maximumFractionDigits: 1 });

/**
 * Reads a figure stored any of the ways this database stores one: a number, a
 * string with a point, a string with an Italian comma ("27,5"), a grouped
 * string ("12.400,00" or "12,400.00"). Returns null when there is no figure.
 */
export function parseNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = String(value).trim().replace(/\s|€/g, '');
  if (!text) return null;
  let normalised = text;
  const lastComma = text.lastIndexOf(',');
  const lastPoint = text.lastIndexOf('.');
  if (lastComma > -1 && lastPoint > -1) {
    // both separators: the last one is the decimal mark
    normalised =
      lastComma > lastPoint
        ? text.replace(/\./g, '').replace(',', '.')
        : text.replace(/,/g, '');
  } else if (lastComma > -1) {
    // one comma: a decimal mark when 1–2 digits follow it, else a group
    normalised = /,\d{1,2}$/.test(text)
      ? text.replace(',', '.')
      : text.replace(/,/g, '');
  }
  const n = Number(normalised);
  return Number.isFinite(n) ? n : null;
}

const sign = (n) => (n < 0 ? '−' : '');

/** `€12.400,00`; an empty string for no figure (a numeric column stays blank). */
export function fMoney(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  return `${sign(n)}€${money.format(Math.abs(n))}`;
}

/**
 * A MONEY FIGURE IN WHOLE EURO: `€12.400`. Where the two-decimal rule of
 * DESIGN.md §3 gives way: a figure read at a glance against a target or a
 * plan (the account managers' report, the budget form's captions). NOT for
 * a ledger: the costs report's twelve-month grid used it from 09/10/2026
 * and the owner asked for the cents back on 14/10 — a cost is read to the
 * cent.
 */
export function fMoneyWhole(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  const whole = Math.round(Math.abs(n));
  return `${n < 0 && whole > 0 ? '−' : ''}€${integer.format(whole)}`;
}

/**
 * A MONEY FIGURE WITH NO SYMBOL: `12.400,00`. What a money FIELD shows, where
 * the euro is an adornment at the field's edge rather than part of the figure
 * (DESIGN.md §16), and what a money field writes back on blur.
 */
export function fAmount(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  return `${sign(n)}${money.format(Math.abs(n))}`;
}

/**
 * A UNIT PRICE: `€0,045` · `€0,06`.
 *
 * The money rule of DESIGN.md §3 is two decimals, and for a total that is
 * right. A rate per word lives in the third: of the 25.355 rates translators
 * have declared, the working band is €0,01–€0,15, so €0,045 and €0,054 are
 * two different prices that two decimals would both round to €0,05 — the one
 * thing the Rate column exists to tell apart. Same separators, same `tnum`:
 * two decimals always, the third only where it carries information.
 */
export function fRate(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  return `${sign(n)}€${unitPrice.format(Math.abs(n))}`;
}

/**
 * A PLAIN ONE-DECIMAL FIGURE: `4,6` — a rating out of five, a score, an
 * average. It is a figure like any other, so it wears the Italian comma
 * (DESIGN.md §3); `toFixed(1)` writes an English point.
 */
export function fDecimal(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  return `${sign(n)}${oneDecimal.format(Math.abs(n))}`;
}

/** `34,2%`. */
export function fPercent(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  return `${sign(n)}${oneDecimal.format(Math.abs(n))}%`;
}

/**
 * A CHANGE, as a percentage with its sign: `+12,4%` · `−8,0%` · `0,0%`.
 *
 * A plain `fPercent` writes the minus and nothing for a rise, which is right
 * for a rate (34,2% of projects) and wrong for a movement — on a report
 * column headed "%" the reader needs to see at a glance which way it went
 * (DESIGN.md §3; the glyph and the tone ink go with it, §4 "colour is never
 * the only signal"). Returns an empty string where no percentage exists, so a
 * numeric column stays blank rather than claiming a figure.
 */
export function fDeltaPercent(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  if (n > 0) return `+${oneDecimal.format(n)}%`;
  return `${sign(n)}${oneDecimal.format(Math.abs(n))}%`;
}

/** `1.240,5 h`. */
export function fHours(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  return `${sign(n)}${oneDecimal.format(Math.abs(n))} h`;
}

/** "12.4k words", "412k words", "1.2M words" — a figure for a strip cell. */
export function fWordsShort(value) {
  const n = parseNumber(value) || 0;
  const one = (v) => String(Math.round(v * 10) / 10);
  if (n >= 1e6) return `${one(n / 1e6)}M words`;
  if (n >= 1e4) return `${Math.round(n / 1e3)}k words`;
  if (n >= 1e3) return `${one(n / 1e3)}k words`;
  return `${Math.round(n).toLocaleString('en-GB')} words`;
}

/** "€86,4k", "€1,2M", "€412,00" — a money figure for a strip cell or a count line. */
export function fMoneyShort(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${sign(n)}€${oneDecimal.format(abs / 1e6)}M`;
  if (abs >= 1e4) return `${sign(n)}€${integer.format(abs / 1e3)}k`;
  if (abs >= 1e3) return `${sign(n)}€${oneDecimal.format(abs / 1e3)}k`;
  return fMoney(n);
}

/** `1.284` — grouped from four digits, no decimals. */
export function fCount(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  return `${sign(n)}${integer.format(Math.abs(n))}`;
}

/**
 * A FILE SIZE, Italian: `7,3 MB` · `912 kB` · `1,2 GB` · `0 B`.
 *
 * Decimal units, which is what S3 reports and what the operating system shows
 * beside a download. The portal's `fData` (numeral, `0.0 b`) wrote "7.3 MB"
 * with an English point beside Italian money on the same row, and printed
 * "0.0 b" for a vault that holds nothing — a figure, where the honest answer
 * is a word the caller chooses. Bytes and kilobytes are whole; from a
 * megabyte up one decimal, because 1 MB and 1,4 MB are different files.
 */
export function fBytes(value) {
  const n = parseNumber(value);
  if (n === null) return '';
  const abs = Math.abs(n);
  if (abs < 1e3) return `${sign(n)}${integer.format(abs)} B`;
  if (abs < 1e6) return `${sign(n)}${integer.format(abs / 1e3)} kB`;
  if (abs < 1e9) return `${sign(n)}${upToOne.format(abs / 1e6)} MB`;
  if (abs < 1e12) return `${sign(n)}${upToOne.format(abs / 1e9)} GB`;
  return `${sign(n)}${upToOne.format(abs / 1e12)} TB`;
}

// ---------------------------------------------------------------------------
// DAYS, counted between midnights: something sent late on the 10th is two
// days old on the morning of the 12th, not "yesterday" (the blog, 12/10/2026).

const DAY = 86400000;
const midnight = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** Whole calendar days from today to `date`: negative in the past, null if unknown. */
export function daysFromToday(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((midnight(d) - midnight(new Date())) / DAY);
}

/** How long ago, in words: "Today", "Yesterday", "3 days ago", "5 weeks ago". */
export function fDaysAgo(date) {
  const offset = daysFromToday(date);
  if (offset === null) return '';
  const days = -offset;
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 60) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? 'A week ago' : `${weeks} weeks ago`;
  }
  const months = Math.floor(days / 30);
  if (months < 24) return `${months} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}
